/**
 * useWorkflowBuilder Hook
 *
 * Custom hook for managing workflow builder state with React Flow.
 * Handles node/edge operations, validation, and persistence.
 */

import { useState, useCallback, useMemo } from 'react';
import {
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Edge,
  Node,
  NodeChange,
  EdgeChange,
} from 'reactflow';
import type {
  WorkflowDefinition,
  WorkflowNode,
  WorkflowEdge,
  WorkflowViewport,
} from '../types';
import type { WorkflowNodeCancellation, WorkflowNodeType } from '@troia-v3/types';
import { WORKFLOW_CANCELLATION_EVENTS, nodeTypeAcceptsFilters } from '@troia-v3/types';
import {
  calculateAvailableContext,
  calculateContextAfterNode,
  EMPTY_CONTEXT,
  validateNodePlacement,
  NODE_TYPE_SCHEMAS,
} from '../utils/node-schemas';
import type { AvailableContext } from '../utils/node-schemas';
import type { FilterCondition } from '../components/FilterRow';
import { CANCELLATION_EVENT_LABELS, defaultTargetLabel } from '../utils/cancellation-events';
import { convertNodes, convertEdges } from './useNodeContext';

// Default viewport (zoom 0.87 = ligeiramente menor que o padrão 1.0)
const DEFAULT_VIEWPORT: WorkflowViewport = { x: 0, y: 0, zoom: 0.87 };

/**
 * Flow node data interface
 */
interface FlowNodeData {
  label?: string;
  type: string;
  config?: Record<string, any>;
  description?: string;
  icon?: string;
  /** Aba Filtros dos nós não-gatilho (Fase 3 liga o motor). */
  filters?: FilterCondition[];
  /** Aba Cancelamento (todo nó, gatilho incluído) — spec 2026-09-14, D1. */
  cancellation?: WorkflowNodeCancellation;
}

// Node ID generator
let nodeIdCounter = 0;
const generateNodeId = () => `node_${++nodeIdCounter}_${Date.now()}`;

// Edge ID generator
const generateEdgeId = (source: string, target: string) =>
  `edge_${source}_${target}_${Date.now()}`;

/**
 * Convert React Flow nodes to workflow definition nodes
 */
const flowNodesToWorkflowNodes = (flowNodes: Node[]): WorkflowNode[] => {
  return flowNodes.map((node) => ({
    id: node.id,
    type: node.data?.type || node.type || 'unknown',
    position: node.position,
    data: {
      label: node.data?.label || '',
      config: node.data?.config || {},
      ...(node.data?.filters !== undefined ? { filters: node.data.filters } : {}),
      ...(node.data?.cancellation !== undefined ? { cancellation: node.data.cancellation } : {}),
    },
  }));
};

/**
 * Convert React Flow edges to workflow definition edges
 */
const flowEdgesToWorkflowEdges = (flowEdges: Edge[]): WorkflowEdge[] => {
  return flowEdges.map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle || undefined,
    targetHandle: edge.targetHandle || undefined,
    label: typeof edge.label === 'string' ? edge.label : undefined,
  }));
};

/**
 * Convert workflow definition nodes to React Flow nodes
 */
const workflowNodesToFlowNodes = (workflowNodes: WorkflowNode[]): Node[] => {
  return workflowNodes.map((node) => ({
    id: node.id,
    type: getReactFlowNodeType(node.type),
    position: node.position,
    data: {
      label: node.data?.label || '',
      type: node.type,
      config: node.data?.config || {},
      ...(node.data?.filters !== undefined ? { filters: node.data.filters } : {}),
      ...(node.data?.cancellation !== undefined ? { cancellation: node.data.cancellation } : {}),
    },
  }));
};

/**
 * Convert workflow definition edges to React Flow edges
 */
const workflowEdgesToFlowEdges = (workflowEdges: WorkflowEdge[]): Edge[] => {
  return workflowEdges.map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle,
    targetHandle: edge.targetHandle,
    type: 'smoothstep',
    label: edge.label,
    animated: true,
  }));
};

/**
 * Map node type to React Flow custom node type
 */
const getReactFlowNodeType = (nodeType: string): string => {
  // Triggers
  if (nodeType.startsWith('trigger_')) return 'triggerNode';
  // Skill entry point - rendered as a trigger-style node
  if (nodeType === 'skill_input') return 'triggerNode';
  // Skill exit point - rendered as an action-style node
  if (nodeType === 'skill_output') return 'actionNode';
  // Actions
  if (nodeType.startsWith('action_')) return 'actionNode';
  // AI Agent
  if (nodeType === 'ai_agent') return 'aiAgentNode';
  // AI Agent Inline
  if (nodeType === 'ai_agent_inline') return 'aiAgentInlineNode';
  // Control
  if (nodeType.startsWith('control_')) return 'controlNode';
  // Default
  return 'default';
};

/**
 * Validation result interface
 */
export interface ValidationResult {
  errors: string[];
  errorNodeIds: string[];
}

/**
 * Validate workflow definition
 *
 * Exportada (além do uso interno do hook) para ser testada isoladamente —
 * ver `validacao-i8-cancelamento.test.ts`.
 */
export const validateWorkflow = (nodes: Node[], edges: Edge[]): ValidationResult => {
  const errors: string[] = [];
  const errorNodeIds: Set<string> = new Set();

  // Must have at least one entry point (trigger_* OR skill_input)
  const triggerNodes = nodes.filter(
    (n) => n.data?.type?.startsWith('trigger_') || n.data?.type === 'skill_input'
  );
  if (triggerNodes.length === 0) {
    errors.push('O workflow deve ter pelo menos um gatilho (trigger) ou entrada de skill');
    // Se não há trigger, todos os nodes estão em "erro" de certa forma
    // Mas vamos apenas indicar que falta um trigger
  }

  // All nodes (except entry points) should have incoming connections
  const nodesWithIncoming = new Set(edges.map((e) => e.target));
  const orphanNodes = nodes.filter(
    (n) =>
      !n.data?.type?.startsWith('trigger_') &&
      n.data?.type !== 'skill_input' &&
      !nodesWithIncoming.has(n.id)
  );
  if (orphanNodes.length > 0) {
    const nodeLabel = orphanNodes.length === 1 ? 'node não possui' : 'nodes não possuem';
    errors.push(`${orphanNodes.length} ${nodeLabel} conexão de entrada`);
    // Adiciona IDs dos nodes órfãos
    orphanNodes.forEach((n) => errorNodeIds.add(n.id));
  }

  // Check for cycles (simple detection)
  // This is a basic check; more sophisticated cycle detection may be needed
  const visited = new Set<string>();
  const recursionStack = new Set<string>();
  const cycleNodes = new Set<string>();

  const hasCycle = (nodeId: string, path: string[] = []): boolean => {
    visited.add(nodeId);
    recursionStack.add(nodeId);
    path.push(nodeId);

    const outgoingEdges = edges.filter((e) => e.source === nodeId);
    for (const edge of outgoingEdges) {
      if (!visited.has(edge.target)) {
        if (hasCycle(edge.target, [...path])) return true;
      } else if (recursionStack.has(edge.target)) {
        // Encontrou ciclo - adiciona todos os nodes do ciclo
        const cycleStartIndex = path.indexOf(edge.target);
        if (cycleStartIndex !== -1) {
          path.slice(cycleStartIndex).forEach((id) => cycleNodes.add(id));
        }
        cycleNodes.add(edge.target);
        return true;
      }
    }

    recursionStack.delete(nodeId);
    return false;
  };

  for (const node of triggerNodes) {
    visited.clear();
    recursionStack.clear();
    if (hasCycle(node.id)) {
      errors.push('O workflow contém um ciclo (loop infinito)');
      // Adiciona nodes do ciclo aos erros
      cycleNodes.forEach((id) => errorNodeIds.add(id));
      break;
    }
  }

  // Check all nodes have valid configuration
  for (const node of nodes) {
    if (!node.data?.type) {
      const nodeLabel = node.data?.label || node.id;
      errors.push(`O node "${nodeLabel}" não possui tipo definido`);
      errorNodeIds.add(node.id);
    }
  }

  // Horário comercial (businessHours) — campos exigidos quando enabled=true,
  // compartilhados pelos nodes de saída (BUSINESS_HOURS_NODE_TYPES).
  const bhEnabled = (c: Record<string, unknown>): boolean =>
    (c['businessHours'] as { enabled?: boolean } | undefined)?.enabled === true;
  const BUSINESS_HOURS_FIELDS: Array<{ field: string; label: string; condition?: (config: Record<string, unknown>) => boolean }> = [
    { field: 'businessHours.weekdays', label: 'Horário comercial: dias da semana', condition: bhEnabled },
    { field: 'businessHours.startTime', label: 'Horário comercial: início', condition: bhEnabled },
    { field: 'businessHours.endTime', label: 'Horário comercial: fim', condition: bhEnabled },
  ];

  // Validate required config fields per node type
  const NODE_REQUIRED_FIELDS: Partial<Record<string, Array<{ field: string; label: string; condition?: (config: Record<string, unknown>) => boolean }>>> = {
    trigger_inactivity: [
      { field: 'entityType', label: 'Tipo de entidade' },
      { field: 'inactivityPeriod', label: 'Período de inatividade' },
    ],
    trigger_date_field: [
      { field: 'entityType', label: 'Tipo de entidade' },
      { field: 'dateField', label: 'Campo de data' },
    ],
    trigger_event: [
      { field: 'eventType', label: 'Tipo de evento' },
    ],
    trigger_schedule: [
      { field: 'cronExpression', label: 'Expressão cron' },
    ],
    action_send_message: [
      { field: 'message', label: 'Mensagem', condition: (c) => c['messageType'] === 'text' || !c['messageType'] },
      { field: 'mediaUrl', label: 'URL da mídia', condition: (c) => c['messageType'] !== undefined && c['messageType'] !== 'text' },
      { field: 'channelId', label: 'Canal', condition: (c) => c['targetType'] === 'new_conversation' },
      { field: 'contactId', label: 'Contato', condition: (c) => c['targetType'] === 'new_conversation' && (c['contactSource'] === 'specific' || c['contactSource'] === 'variable') },
      ...BUSINESS_HOURS_FIELDS,
    ],
    action_send_media: [
      { field: 'mediaUrl', label: 'URL da mídia' },
      ...BUSINESS_HOURS_FIELDS,
    ],
    action_send_email: [
      { field: 'to', label: 'Destinatário' },
      { field: 'subject', label: 'Assunto' },
      ...BUSINESS_HOURS_FIELDS,
    ],
    action_send_template: [
      { field: 'channelId', label: 'Canal' },
      { field: 'templateId', label: 'Template' },
      ...BUSINESS_HOURS_FIELDS,
    ],
    action_create_lead: [
      { field: 'funnelId', label: 'Funil' },
      // Mesma regra do action_send_message: nos modos 'specific' (dropdown) e
      // 'variable' (template do fluxo) o contato NÃO vem do contexto, então o
      // campo é obrigatório — sem ele o backend lança ValidationError em runtime.
      { field: 'contactId', label: 'Contato', condition: (c) => c['contactSource'] === 'specific' || c['contactSource'] === 'variable' },
    ],
    // "Canal de origem do lead" (2026-09-30) dispensa o canal fixo.
    action_create_conversation: [
      { field: 'channelId', label: 'Canal', condition: (c) => c['channelSource'] !== 'lead_origin' },
    ],
    // Mesmas regras do backend (`transferConversationConfigSchema`): o destino
    // escolhido é obrigatório, e a conversa só quando vem de variável.
    action_transfer_conversation: [
      { field: 'transferTo', label: 'Transferir para' },
      { field: 'userId', label: 'Usuário', condition: (c) => c['transferTo'] === 'user' },
      { field: 'teamId', label: 'Equipe', condition: (c) => c['transferTo'] === 'team' },
      { field: 'conversationId', label: 'Conversa', condition: (c) => c['conversationSource'] === 'variable' },
    ],
    // Mesma regra do backend (`satisfactionSurveyConfigSchema`): só a conversa
    // por variável exige campo — o motivo tem padrão ('resolved').
    action_satisfaction_survey: [
      { field: 'conversationId', label: 'Conversa', condition: (c) => c['conversationSource'] === 'variable' },
    ],
    // Pergunta (2026-09-28): o resto (opções, faixa, prazo, palavra repetida
    // entre opções) é conferido pelo backend (`askQuestionConfigSchema`) na
    // validação ao vivo do editor.
    action_ask_question: [
      { field: 'message', label: 'Pergunta' },
      { field: 'answerType', label: 'Tipo de resposta' },
      { field: 'options', label: 'Opções', condition: (c) => c['answerType'] === 'options' },
      { field: 'conversationId', label: 'Conversa', condition: (c) => c['conversationSource'] === 'variable' },
    ],
    // Perguntar formulário (2026-09-28): prazo e tentativas são conferidos
    // pelo backend (`askFormConfigSchema`) na validação ao vivo do editor.
    action_ask_form: [
      { field: 'formId', label: 'Formulário' },
      { field: 'conversationId', label: 'Conversa', condition: (c) => c['conversationSource'] === 'variable' },
    ],
    // Modo "Gerar cupom do lead" (2026-09-29): sem nome de sequência; o lead
    // só é campo quando vem de variável.
    action_next_number: [
      { field: 'sequenceName', label: 'Nome da sequência', condition: (c) => c['target'] !== 'lead_coupon' },
      { field: 'leadId', label: 'Lead', condition: (c) => c['target'] === 'lead_coupon' && c['leadSource'] === 'variable' },
    ],
    // O resto (pergunta obrigatória sem valor) só dá para saber com o
    // formulário carregado — o backend confere na validação ao vivo.
    action_save_form_response: [
      { field: 'formId', label: 'Formulário' },
      {
        field: 'leadId',
        label: 'Lead',
        condition: (c) => c['linkToLead'] !== false && (c['leadSource'] === 'variable' || c['leadSource'] === 'specific'),
      },
    ],
    action_create_ticket: [
      { field: 'title', label: 'Título' },
    ],
    action_http_request: [
      { field: 'url', label: 'URL' },
    ],
    action_query_database: [
      { field: 'databaseType', label: 'Tipo de banco' },
      { field: 'databaseNames', label: 'Bancos' },
    ],
    action_add_tag: [
      { field: 'tags', label: 'Tags' },
    ],
    action_remove_tag: [
      { field: 'tags', label: 'Tags' },
    ],
    action_update_lead: [
      { field: 'leadId', label: 'Lead', condition: (c) => c['leadSource'] === 'specific' },
    ],
    action_assign: [
      { field: 'userId', label: 'ID do usuário', condition: (c) => c['assigneeType'] === 'user' },
      { field: 'teamId', label: 'ID da equipe', condition: (c) => c['assigneeType'] === 'team' },
    ],
    action_internal_notification: [
      { field: 'recipientId', label: 'Destinatário' },
      { field: 'title', label: 'Título' },
      ...BUSINESS_HOURS_FIELDS,
    ],
    action_set_variable: [
      { field: 'variableName', label: 'Nome da variável' },
      { field: 'value', label: 'Valor' },
    ],
    ai_agent: [
      { field: 'agentId', label: 'Agente' },
      ...BUSINESS_HOURS_FIELDS,
    ],
    ai_agent_inline: [
      { field: 'systemPrompt', label: 'System prompt' },
      { field: 'model', label: 'Modelo' },
      { field: 'input', label: 'Input Template' },
      ...BUSINESS_HOURS_FIELDS,
    ],
    control_if: [
      { field: 'condition', label: 'Condição' },
    ],
    control_switch: [
      { field: 'field', label: 'Campo a avaliar' },
    ],
    control_loop: [
      { field: 'items', label: 'Array para iterar' },
    ],
    action_voice_clone: [
      { field: 'voiceUrl', label: 'URL do áudio com a voz' },
    ],
    action_voice_tts: [
      { field: 'cloneId', label: 'ID do clone' },
      { field: 'text', label: 'Texto a falar' },
    ],
    action_voice_clone_delete: [
      { field: 'cloneId', label: 'ID do clone' },
    ],
    action_mirror_media: [
      { field: 'url', label: 'URL externa' },
    ],
    action_create_checklist: [
      { field: 'templateId', label: 'Modelo' },
      { field: 'target.mode', label: 'Alvo' },
      { field: 'target.count', label: 'Quantidade de unidades', condition: (c) => (c['target'] as { mode?: string } | undefined)?.mode === 'draw_units' },
      { field: 'target.unitIds', label: 'Unidades', condition: (c) => (c['target'] as { mode?: string } | undefined)?.mode === 'fixed_units' },
      { field: 'assignee.mode', label: 'Responsável' },
      { field: 'assignee.userId', label: 'Usuário responsável', condition: (c) => (c['assignee'] as { mode?: string } | undefined)?.mode === 'fixed_user' },
    ],
    action_find_unit: [
      { field: 'document', label: 'Documento' },
      { field: 'saveAs', label: 'Salvar como' },
    ],
    action_find_user: [
      { field: 'saveAs', label: 'Salvar como' },
    ],
    action_find_contact: [
      { field: 'saveAs', label: 'Salvar como' },
    ],
    action_url_to_pdf: [
      { field: 'url', label: 'Endereço da página' },
      { field: 'saveAs', label: 'Salvar resultado como' },
    ],
    action_nfe_pdf: [
      { field: 'xml', label: 'XML da nota' },
      { field: 'saveAs', label: 'Salvar resultado como' },
    ],
  };

  for (const node of nodes) {
    const nodeType = node.data?.type as string | undefined;
    if (!nodeType) continue;

    const requiredFields = NODE_REQUIRED_FIELDS[nodeType];
    if (!requiredFields) continue;

    const config = (node.data?.config ?? {}) as Record<string, unknown>;
    const nodeLabel = node.data?.label || nodeType;

    for (const req of requiredFields) {
      // Skip if condition exists and is not met
      if (req.condition && !req.condition(config)) continue;

      // Suporta dot-path (ex: 'businessHours.startTime') além de campo plano
      const value = req.field.split('.').reduce<unknown>(
        (acc, key) =>
          acc !== null && typeof acc === 'object' ? (acc as Record<string, unknown>)[key] : undefined,
        config,
      );
      // Arrays vazios contam como ausentes (ex: tags, databaseNames)
      const isEmpty = value === undefined || value === null || value === ''
        || (Array.isArray(value) && value.length === 0);

      if (isEmpty) {
        errors.push(`"${nodeLabel}": campo "${req.label}" é obrigatório`);
        errorNodeIds.add(node.id);
      }
    }

    // Janela do horário comercial: início deve ser antes do fim (overnight fora
    // do MVP — paridade com validateBusinessHoursConfigs do backend).
    if (bhEnabled(config)) {
      const bh = config['businessHours'] as { startTime?: string; endTime?: string };
      if (
        typeof bh.startTime === 'string' &&
        typeof bh.endTime === 'string' &&
        bh.startTime !== '' &&
        bh.endTime !== '' &&
        bh.startTime >= bh.endTime
      ) {
        errors.push(`"${nodeLabel}": horário comercial com janela inválida (início deve ser antes do fim)`);
        errorNodeIds.add(node.id);
      }
    }
  }

  // Validate context compatibility between connected nodes
  const convertedNodes = nodes.map((n) => ({
    id: n.id,
    type: (n.data?.type || n.type) as WorkflowNodeType,
    data: { config: n.data?.config as Record<string, unknown> | undefined },
  }));
  const convertedEdges = edges.map((e) => ({
    source: e.source,
    target: e.target,
    sourceHandle: e.sourceHandle || undefined,
  }));

  for (const node of nodes) {
    const nodeType = (node.data?.type || node.type) as WorkflowNodeType;
    if (!nodeType) continue;

    const nodeLabel = node.data?.label || nodeType;
    const isTrigger = nodeType.startsWith('trigger_');
    // I8 (spec 2026-09-14, Fase 2): regra da aba Cancelamento que se refere a
    // uma entidade que este node não conhece, sem "A quem se refere"
    // preenchido — sem um alvo explícito, o motor não tem com o que comparar
    // em runtime. Vale para QUALQUER node, gatilhos incluídos: um gatilho de
    // agenda (entityType 'lead', por exemplo) com regra "Mensagem recebida"
    // não tem `conversation` no contexto que ele mesmo fornece.
    //
    // Um gatilho não tem predecessores — `calculateAvailableContext` devolveria
    // sempre vazio para ele, o que reprova o caso normal (gatilho "Mensagem
    // recebida" com regra "Mensagem recebida" e sem `refersTo`). O contexto
    // certo para um gatilho é o que ELE PRÓPRIO provê, calculado a partir do
    // vazio; para os demais nós o armamento acontece ANTES do nó rodar, então
    // o contexto continua sendo o dos predecessores.
    const availableContext = isTrigger
      ? calculateContextAfterNode(
          EMPTY_CONTEXT,
          nodeType,
          node.data?.config as { eventType?: string; entityType?: string } | undefined,
        )
      : calculateAvailableContext(convertedNodes, convertedEdges, node.id);

    const rules = (node.data as { cancellation?: WorkflowNodeCancellation } | undefined)?.cancellation?.rules ?? [];
    for (const rule of rules) {
      if (rule.refersTo !== undefined && rule.refersTo !== '') continue;
      const entity = WORKFLOW_CANCELLATION_EVENTS[rule.eventType]?.entity;
      if (entity !== undefined && !availableContext[entity as keyof AvailableContext]) {
        errors.push(`"${nodeLabel}": a regra "${CANCELLATION_EVENT_LABELS[rule.eventType]}" precisa de ${defaultTargetLabel(rule.eventType)}, que este nó não conhece — informe "A quem se refere"`);
        errorNodeIds.add(node.id);
      }
    }

    // Filtros — Fase 3 (spec 2026-09-14 §5.3): `data.filters` só existe em
    // nós filtráveis (`nodeTypeAcceptsFilters`). Sobrou de um nodeType trocado
    // depois de preenchido (Condição, Selecionar, etc. não têm aba Filtros) —
    // sem essa trava o filtro fica gravado mas nunca é avaliado em runtime.
    const nodeFilters = (node.data as { filters?: unknown[] } | undefined)?.filters;
    if (Array.isArray(nodeFilters) && nodeFilters.length > 0 && !nodeTypeAcceptsFilters(nodeType)) {
      // M7 (revisão final, Fase 3): texto alinhado ao do backend
      // (`validateNodeFilters` em `validation.ts`) — antes o front dizia só
      // "não tem aba Filtros", sem instruir o que fazer, e o usuário via uma
      // frase ou outra dependendo de onde a checagem pegou (desenho x salvar).
      errors.push(`"${nodeLabel}": este tipo de nó não tem a aba Filtros — remova os filtros configurados`);
      errorNodeIds.add(node.id);
    }

    if (nodeType.startsWith('trigger_')) continue;

    const schema = NODE_TYPE_SCHEMAS[nodeType];
    if (!schema?.input?.requiresContext) continue;

    const requirements = schema.input.requiresContext;
    const hasRequirements = Object.values(requirements).some(Boolean);
    if (!hasRequirements) continue;

    const missing: string[] = [];

    for (const [key, required] of Object.entries(requirements)) {
      if (required && !availableContext[key as keyof AvailableContext]) {
        missing.push(key);
      }
    }

    if (missing.length > 0) {
      errors.push(`"${nodeLabel}": requer contexto não disponível: ${missing.join(', ')}`);
      errorNodeIds.add(node.id);
    }
  }

  return { errors, errorNodeIds: Array.from(errorNodeIds) };
};

export interface UseWorkflowBuilderOptions {
  initialDefinition?: WorkflowDefinition;
  onChange?: (definition: WorkflowDefinition) => void;
  onConnectionBlocked?: (reason: string) => void;
}

export interface UseWorkflowBuilderReturn {
  // React Flow state
  nodes: Node[];
  edges: Edge[];
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (connection: Connection) => void;
  /**
   * Conecta dois nodes diretamente (usado pelo "+" de um node, que adiciona um
   * node novo já ligado à origem). Diferente de `onConnect`, NÃO depende do node
   * de destino já estar no estado — o "+" cria o node e conecta no mesmo tick,
   * quando `nodes` ainda não reflete o node recém-criado.
   */
  connectNodes: (source: string, target: string, sourceHandle?: string) => void;

  // Node operations
  addNode: (type: string, position: { x: number; y: number }, data?: Partial<FlowNodeData>) => Node;
  updateNode: (nodeId: string, data: Partial<FlowNodeData>) => void;
  /**
   * Remove um ou VÁRIOS nós de uma vez, com as ligações que tocam neles. É a
   * única porta de exclusão de nó: uma versão de um nó só, que lesse
   * `selectedNode` do fecho, usaria um valor velho entre as chamadas de um laço
   * e faria um render por nó.
   */
  deleteNodes: (nodeIds: string[]) => void;
  duplicateNode: (nodeId: string) => void;

  // Edge operations
  deleteEdge: (edgeId: string) => void;
  updateEdge: (edgeId: string, data: Partial<Edge>) => void;
  /**
   * Remove as ligações que saem de UMA saída de um nó (spec 2026-09-20 §7.2):
   * apagar a última regra do Aguardar tira a saída "Cancelado", e a ligação
   * pendurada nela não pode ficar apontando para uma saída que não existe mais.
   */
  removeEdgesFromHandle: (nodeId: string, sourceHandle: string) => void;

  // Selection
  selectedNode: Node | null;
  selectedEdge: Edge | null;
  setSelectedNode: (node: Node | null) => void;
  setSelectedEdge: (edge: Edge | null) => void;
  clearSelection: () => void;

  // State management
  isDirty: boolean;
  setIsDirty: (dirty: boolean) => void;
  reset: () => void;
  loadDefinition: (definition: WorkflowDefinition) => void;

  // Validation
  validationErrors: string[];
  errorNodeIds: string[];
  isValid: boolean;
  validate: () => boolean;
  clearValidationErrors: () => void;

  // Viewport
  initialViewport: WorkflowViewport;
  setViewport: (viewport: WorkflowViewport) => void;

  // Export
  getDefinition: (currentViewport?: WorkflowViewport) => WorkflowDefinition;
  toJSON: () => string;
}

/**
 * Workflow Builder Hook
 *
 * Manages the state and operations for the visual workflow editor.
 */
export function useWorkflowBuilder(
  options: UseWorkflowBuilderOptions = {}
): UseWorkflowBuilderReturn {
  const { initialDefinition, onChange, onConnectionBlocked } = options;

  // Initialize nodes and edges from definition
  const initialNodes = initialDefinition?.nodes
    ? workflowNodesToFlowNodes(initialDefinition.nodes)
    : [];
  const initialEdges = initialDefinition?.edges
    ? workflowEdgesToFlowEdges(initialDefinition.edges)
    : [];

  // React Flow state
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Selection state
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);

  // Dirty state
  const [isDirty, setIsDirty] = useState(false);

  // Validation state
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [errorNodeIds, setErrorNodeIds] = useState<string[]>([]);

  // Viewport state - use saved viewport or default
  const [viewport, setViewport] = useState<WorkflowViewport>(
    initialDefinition?.viewport || DEFAULT_VIEWPORT
  );

  // Handle new connections
  const onConnect = useCallback(
    (connection: Connection) => {
      if (connection.source && connection.target) {
        // Find the target node to validate context requirements
        const targetNode = nodes.find((n) => n.id === connection.target);
        if (targetNode) {
          const targetNodeType = (targetNode.data?.type || targetNode.type) as WorkflowNodeType;

          // Calculate available context at the target node (including the new edge)
          const potentialEdge: Edge = {
            id: 'temp-edge',
            source: connection.source,
            target: connection.target,
            sourceHandle: connection.sourceHandle || undefined,
            targetHandle: connection.targetHandle || undefined,
          };

          // Convert nodes and edges to the format expected by calculateAvailableContext
          const convertedNodes = convertNodes(nodes);
          const convertedEdges = convertEdges([...edges, potentialEdge]);

          const availableContext = calculateAvailableContext(convertedNodes, convertedEdges, connection.target);

          // Validate if target node requirements are satisfied
          const validation = validateNodePlacement(targetNodeType, availableContext);

          if (!validation.isValid) {
            // Block connection and notify via callback
            const reason = `O node "${targetNode.data?.label || targetNodeType}" requer contexto não fornecido: ${validation.missingContext.join(', ')}`;
            onConnectionBlocked?.(reason);
            return;
          }
        }

        const newEdge: Edge = {
          id: generateEdgeId(connection.source, connection.target),
          source: connection.source,
          target: connection.target,
          sourceHandle: connection.sourceHandle || undefined,
          targetHandle: connection.targetHandle || undefined,
          type: 'smoothstep',
          animated: true,
        };
        setEdges((eds) => addEdge(newEdge, eds));
        setIsDirty(true);
      }
    },
    [nodes, edges, setEdges, onConnectionBlocked]
  );

  // Conexão herdada do "+": cria a edge diretamente (o node de destino acabou
  // de ser criado e ainda não está em `nodes`, então não dá pra validar
  // contexto aqui — a validação design-time do editor sinaliza depois).
  const connectNodes = useCallback(
    (source: string, target: string, sourceHandle?: string) => {
      const newEdge: Edge = {
        id: generateEdgeId(source, target),
        source,
        target,
        ...(sourceHandle ? { sourceHandle } : {}),
        type: 'smoothstep',
        animated: true,
      };
      setEdges((eds) => addEdge(newEdge, eds));
      setIsDirty(true);
    },
    [setEdges]
  );

  // Default display labels for node types (PT-BR, uppercase)
  const NODE_DEFAULT_LABELS: Record<string, string> = {
    // Triggers
    trigger_webhook: 'Webhook',
    trigger_schedule: 'Agendamento',
    trigger_event: 'Evento',
    trigger_manual: 'Manual',
    trigger_date_field: 'Campo de data',
    trigger_inactivity: 'Inatividade',
    trigger_instagram_comment: 'Comentário Instagram',
    trigger_instagram_mention: 'Menção Instagram',
    // Actions
    action_send_message: 'Enviar mensagem',
    action_send_email: 'Enviar e-mail',
    action_send_template: 'Enviar template',
    action_send_media: 'Enviar mídia',
    action_http_request: 'Requisição HTTP',
    action_query_database: 'Consultar banco',
    action_create_lead: 'Criar lead',
    action_update_lead: 'Atualizar lead',
    action_update_contact: 'Atualizar contato',
    action_add_tag: 'Adicionar tag',
    action_remove_tag: 'Remover tag',
    action_assign: 'Atribuir',
    action_set_variable: 'Definir variável',
    action_create_conversation: 'Criar conversa',
    action_transfer_conversation: 'Transferir conversa',
    action_satisfaction_survey: 'CSAT — Satisfação do Cliente',
    action_ask_question: 'Pergunta',
    action_ask_form: 'Perguntar formulário',
    action_next_number: 'Número sequencial',
    action_save_form_response: 'Salvar formulário',
    action_create_ticket: 'Criar ticket',
    action_internal_notification: 'Notificação interna',
    action_find_leads: 'Buscar leads',
    action_create_database_document: 'Criar documento',
    action_create_checklist: 'Criar checklist',
    action_find_unit: 'Buscar unidade',
    action_find_user: 'Buscar usuário',
    action_find_contact: 'Buscar ou criar contato',
    action_url_to_pdf: 'Gerar PDF de uma página',
    action_nfe_pdf: 'Gerar PDF da nota fiscal',
    // IA
    ai_agent: 'Agente IA',
    ai_agent_inline: 'Agente IA inline',
    // Control
    control_if: 'Condição',
    control_switch: 'Switch',
    control_wait_for: 'Aguardar',
    control_loop: 'Loop',
    control_split: 'Teste A/B',
    // Skills
    skill_input: 'Skill: Entrada',
    skill_output: 'Skill: Saída',
  };

  // Configs default por tipo de node — garante que campos obrigatórios
  // são persistidos desde a criação, evitando fallbacks visuais que mascaram ausência.
  const NODE_DEFAULT_CONFIGS: Record<string, Record<string, unknown>> = {
    trigger_inactivity: {
      entityType: 'conversation',
      inactivityPeriod: 5,
      periodUnit: 'hours',
      maxTriggersPerEntity: 1,
      resetOnActivity: true,
    },
    trigger_date_field: {
      entityType: 'lead',
      dateField: '',
      offsetMinutes: 0,
      offsetDirection: 'before',
    },
    action_transfer_conversation: {
      transferTo: 'user',
      targetSource: 'specific',
    },
    action_satisfaction_survey: {
      closeReason: 'resolved',
    },
    action_ask_question: {
      message: '',
      answerType: 'options',
      options: [
        { id: 'opt_sim', label: 'Sim' },
        { id: 'opt_nao', label: 'Não' },
      ],
      maxRetries: 2,
      timeout: { duration: 24, unit: 'hours' },
    },
    action_ask_form: {
      formId: '',
      maxRetries: 2,
      timeout: { duration: 24, unit: 'hours' },
    },
    action_next_number: {
      sequenceName: '',
      padLength: 4,
      startAt: 1,
    },
    action_save_form_response: {
      formId: '',
      linkToLead: true,
      leadSource: 'context',
      answers: {},
    },
    action_find_unit: {
      saveAs: 'unidade',
    },
    action_find_user: {
      saveAs: 'vendedor',
    },
    action_find_contact: {
      createIfMissing: true,
      saveAs: 'contato',
    },
    action_url_to_pdf: {
      filename: 'documento.pdf',
      singlePage: false,
      saveAs: 'documentoPdf',
    },
    action_nfe_pdf: {
      xml: '{{triggerData.body.documentoFiscal.xml}}',
      saveAs: 'notaPdf',
    },
  };

  // Add a new node
  const addNode = useCallback(
    (type: string, position: { x: number; y: number }, data?: Partial<FlowNodeData>): Node => {
      const defaultConfig = NODE_DEFAULT_CONFIGS[type] ?? {};
      const newNode: Node = {
        id: generateNodeId(),
        type: getReactFlowNodeType(type),
        position,
        data: {
          label: data?.label || NODE_DEFAULT_LABELS[type] || type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
          type,
          config: data?.config || defaultConfig,
          description: data?.description,
          icon: data?.icon,
          ...(data?.cancellation !== undefined ? { cancellation: data.cancellation } : {}),
          ...(data?.filters !== undefined ? { filters: data.filters } : {}),
        },
      };
      setNodes((nds) => [...nds, newNode]);
      setIsDirty(true);
      return newNode;
    },
    [setNodes]
  );

  // Update a node
  const updateNode = useCallback(
    (nodeId: string, data: Partial<FlowNodeData>) => {
      let updatedNode: Node | null = null;
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === nodeId) {
            updatedNode = { ...node, data: { ...node.data, ...data } };
            return updatedNode;
          }
          return node;
        })
      );
      // Sync selectedNode to avoid stale references in config panel
      if (updatedNode && selectedNode?.id === nodeId) {
        setSelectedNode(updatedNode);
      }
      setIsDirty(true);
    },
    [setNodes, selectedNode]
  );

  // Excluir um ou vários nós numa confirmação só (menu do nó e tecla Delete com
  // seleção múltipla — spec 2026-09-20 §5.2). Tudo por atualização funcional:
  // assim não depende de `selectedNode`/`selectedEdge` no fecho, que é o que
  // tornaria a função imprópria para laço.
  const deleteNodes = useCallback(
    (nodeIds: string[]) => {
      if (nodeIds.length === 0) return;
      const doomed = new Set(nodeIds);
      setNodes((nds) => nds.filter((node) => !doomed.has(node.id)));
      setEdges((eds) => eds.filter((edge) => !doomed.has(edge.source) && !doomed.has(edge.target)));
      setSelectedNode((current) => (current !== null && doomed.has(current.id) ? null : current));
      setSelectedEdge((current) =>
        current !== null && (doomed.has(current.source) || doomed.has(current.target)) ? null : current,
      );
      setIsDirty(true);
    },
    [setNodes, setEdges]
  );

  // Duplicate a node
  const duplicateNode = useCallback(
    (nodeId: string) => {
      const nodeToDuplicate = nodes.find((n) => n.id === nodeId);
      if (nodeToDuplicate) {
        const newNode: Node = {
          ...nodeToDuplicate,
          id: generateNodeId(),
          position: {
            x: nodeToDuplicate.position.x + 50,
            y: nodeToDuplicate.position.y + 50,
          },
          selected: false,
        };
        setNodes((nds) => [...nds, newNode]);
        setIsDirty(true);
      }
    },
    [nodes, setNodes]
  );

  // Delete an edge
  const deleteEdge = useCallback(
    (edgeId: string) => {
      setEdges((eds) => eds.filter((edge) => edge.id !== edgeId));
      if (selectedEdge?.id === edgeId) {
        setSelectedEdge(null);
      }
      setIsDirty(true);
    },
    [setEdges, selectedEdge]
  );

  // Remove as ligações que saem de UMA saída do nó (spec 2026-09-20 §7.2).
  // As DUAS pontas contam: filtrar só por `source` apagaria também a ligação
  // do lado verde, que continua valendo.
  const removeEdgesFromHandle = useCallback(
    (nodeId: string, sourceHandle: string) => {
      setEdges((eds) => eds.filter((edge) => !(edge.source === nodeId && edge.sourceHandle === sourceHandle)));
      setSelectedEdge((current) =>
        current !== null && current.source === nodeId && current.sourceHandle === sourceHandle ? null : current,
      );
      setIsDirty(true);
    },
    [setEdges]
  );

  // Update an edge
  const updateEdge = useCallback(
    (edgeId: string, data: Partial<Edge>) => {
      setEdges((eds) =>
        eds.map((edge) =>
          edge.id === edgeId ? { ...edge, ...data } : edge
        )
      );
      setIsDirty(true);
    },
    [setEdges]
  );

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
    setNodes((nds) => nds.map((n) => ({ ...n, selected: false })));
    setEdges((eds) => eds.map((e) => ({ ...e, selected: false })));
  }, [setNodes, setEdges]);

  // Reset to initial state
  const reset = useCallback(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
    setSelectedNode(null);
    setSelectedEdge(null);
    setIsDirty(false);
    setValidationErrors([]);
    setErrorNodeIds([]);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  // Load a new definition
  const loadDefinition = useCallback(
    (definition: WorkflowDefinition) => {
      const newNodes = workflowNodesToFlowNodes(definition.nodes || []);
      const newEdges = workflowEdgesToFlowEdges(definition.edges || []);
      setNodes(newNodes);
      setEdges(newEdges);
      setSelectedNode(null);
      setSelectedEdge(null);
      setIsDirty(false);
      setValidationErrors([]);
      setErrorNodeIds([]);
      // Load saved viewport or use default
      setViewport(definition.viewport || DEFAULT_VIEWPORT);
    },
    [setNodes, setEdges]
  );

  // Validate the workflow
  const validate = useCallback((): boolean => {
    const result = validateWorkflow(nodes, edges);
    setValidationErrors(result.errors);
    setErrorNodeIds(result.errorNodeIds);
    return result.errors.length === 0;
  }, [nodes, edges]);

  // Clear validation errors
  const clearValidationErrors = useCallback(() => {
    setValidationErrors([]);
    setErrorNodeIds([]);
  }, []);

  // Check if valid (memoized)
  const isValid = useMemo(() => {
    return validationErrors.length === 0;
  }, [validationErrors]);

  // Get the workflow definition
  const getDefinition = useCallback((currentViewport?: WorkflowViewport): WorkflowDefinition => {
    return {
      nodes: flowNodesToWorkflowNodes(nodes),
      edges: flowEdgesToWorkflowEdges(edges),
      variables: {}, // TODO: Add variable management
      viewport: currentViewport || viewport,
    };
  }, [nodes, edges, viewport]);

  // Export as JSON
  const toJSON = useCallback((): string => {
    return JSON.stringify(getDefinition(), null, 2);
  }, [getDefinition]);

  // Notify on changes
  const wrappedOnNodesChange = useCallback(
    (changes: NodeChange[]) => {
      onNodesChange(changes);

      // Só marca como dirty se houver mudanças reais (não seleção ou dimensões)
      const hasRealChanges = changes.some(
        (change) =>
          change.type === 'remove' ||
          change.type === 'add' ||
          (change.type === 'position' && change.dragging === false) // Posição final após arrasto
      );

      if (hasRealChanges) {
        setIsDirty(true);
        if (onChange) {
          onChange(getDefinition());
        }
      }
    },
    [onNodesChange, onChange, getDefinition]
  );

  const wrappedOnEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      onEdgesChange(changes);

      // Só marca como dirty se houver mudanças reais (não seleção)
      const hasRealChanges = changes.some(
        (change) =>
          change.type === 'remove' ||
          change.type === 'add'
      );

      if (hasRealChanges) {
        setIsDirty(true);
        if (onChange) {
          onChange(getDefinition());
        }
      }
    },
    [onEdgesChange, onChange, getDefinition]
  );

  return {
    // React Flow state
    nodes,
    edges,
    onNodesChange: wrappedOnNodesChange,
    onEdgesChange: wrappedOnEdgesChange,
    onConnect,
    connectNodes,

    // Node operations
    addNode,
    updateNode,
    deleteNodes,
    duplicateNode,

    // Edge operations
    deleteEdge,
    updateEdge,
    removeEdgesFromHandle,

    // Selection
    selectedNode,
    selectedEdge: selectedEdge as Edge | null,
    setSelectedNode,
    setSelectedEdge: setSelectedEdge as (edge: Edge | null) => void,
    clearSelection,

    // State management
    isDirty,
    setIsDirty,
    reset,
    loadDefinition,

    // Validation
    validationErrors,
    errorNodeIds,
    isValid,
    validate,
    clearValidationErrors,

    // Viewport
    initialViewport: viewport,
    setViewport,

    // Export
    getDefinition,
    toJSON,
  };
}

export default useWorkflowBuilder;
